import React from "react";
import { X, Minus } from "lucide-react";
import { useWM } from "./store";
import { APPS } from "./apps";
import { useIsMobile } from "./useIsMobile";

/**
 * Browser-like tab bar — shows every open app as a tab.
 * - Clicking a tab focuses it (makes it the visible tab)
 * - × closes the tab (closes the window)
 * - The dock at the bottom continues to launch new tabs.
 */
export default function TabBar() {
  const { windows, focused, focus, close, minimize } = useWM();
  const isMobile = useIsMobile();
  if (!windows || windows.length === 0) return null;

  return (
    <div
      data-testid="tab-bar"
      className="absolute left-0 right-0 z-[400] flex items-end gap-1 px-2 pt-1 select-none overflow-x-auto no-scrollbar"
      style={{
        top: 28, // sit just under the TopBar
        background: "linear-gradient(180deg, rgba(8,11,22,0.92), rgba(8,11,22,0.6))",
        borderBottom: "1px solid rgba(167,139,250,0.18)",
        backdropFilter: "blur(10px)",
        height: isMobile ? 32 : 38,
      }}
    >
      {windows.map((w) => {
        const meta = APPS.find((a) => a.id === w.appId);
        const Icon = meta?.icon;
        const isActive = focused === w.id;
        return (
          <button
            key={w.id}
            data-testid={`tab-${w.appId}-${w.id}`}
            onClick={() => focus(w.id)}
            title={w.title}
            className={`group flex items-center gap-2 ${isMobile ? "pl-2 pr-1 h-[26px] min-w-[88px] max-w-[140px]" : "pl-3 pr-1 h-[30px] min-w-[120px] max-w-[220px]"} transition relative flex-shrink-0`}
            style={{
              background: isActive
                ? "linear-gradient(180deg, rgba(167,139,250,0.18), rgba(8,11,22,0.5))"
                : "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderBottomColor: isActive ? "rgba(167,139,250,0.6)" : "rgba(255,255,255,0.06)",
              borderTopLeftRadius: 6,
              borderTopRightRadius: 6,
              color: isActive ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.65)",
            }}
          >
            {Icon ? <Icon size={isMobile ? 11 : 13} strokeWidth={1.8} /> : null}
            <span className={`flex-1 truncate ${isMobile ? "text-[10px]" : "text-[11.5px]"}`}>{w.title}</span>
            {!isMobile && (
              <span
                role="button"
                data-testid={`tab-min-${w.id}`}
                onClick={(e) => { e.stopPropagation(); minimize(w.id); }}
                title="Minimize"
                className="p-1 rounded hover:bg-white/10 opacity-60 group-hover:opacity-100"
              >
                <Minus size={10} />
              </span>
            )}
            <span
              role="button"
              data-testid={`tab-close-${w.id}`}
              onClick={(e) => { e.stopPropagation(); close(w.id); }}
              title="Close"
              className="p-1 rounded hover:bg-[#f87171]/20 opacity-60 group-hover:opacity-100"
            >
              <X size={isMobile ? 9 : 10} />
            </span>
            {isActive && (
              <span
                aria-hidden
                className="absolute left-0 right-0 bottom-[-1px]"
                style={{ height: 2, background: "linear-gradient(120deg,#a78bfa,#60a5fa)" }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
